import React, { useEffect, useMemo, useRef, useState, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { appApi, useGetChatContactsQuery, useGetChatConversationsQuery } from "../../../store/appApi";
import { selectUser } from "../../../store/authSlice";
import {
  getOrCreateDmApi,
  getMessagesApi,
  sendMessageApi,
  markReadApi,
  getChatSocket,
} from "../../../services/chat";

const panelClass = "rounded-2xl border border-gray-200 bg-white shadow-sm";

const initials = (name) =>
  (name || "")
    .trim()
    .split(/\s+/)
    .filter(Boolean)
    .map((w) => w[0])
    .join("")
    .slice(0, 2)
    .toUpperCase() || "?";

const fmtTime = (d) =>
  d ? new Date(d).toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" }) : "";

const fmtDay = (d) =>
  d ? new Date(d).toLocaleDateString("en-IN", { day: "2-digit", month: "short" }) : "";

const ChatPage = () => {
  const dispatch = useDispatch();
  const me = useSelector(selectUser);
  const meId = me?.localId;

  const { data: convData, isLoading: convLoading } = useGetChatConversationsQuery();
  const conversations = useMemo(() => convData?.conversations || [], [convData]);

  const [contactsOpen, setContactsOpen] = useState(false);
  const { data: contactsData } = useGetChatContactsQuery(undefined, { skip: !contactsOpen });
  const contacts = contactsData?.contacts || [];

  const [activeId, setActiveId] = useState(null);
  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [text, setText] = useState("");
  const [files, setFiles] = useState([]);
  const [sending, setSending] = useState(false);
  const fileInputRef = useRef(null);
  const bottomRef = useRef(null);

  const active = conversations.find((c) => c._id === activeId) || null;

  const refreshConversations = useCallback(
    () => dispatch(appApi.util.invalidateTags(["ChatConversations"])),
    [dispatch],
  );

  // Ref mirror so the socket handler (registered once, below) always sees
  // the latest open conversation without re-subscribing on every switch.
  const activeIdRef = useRef(null);
  useEffect(() => {
    activeIdRef.current = activeId;
  }, [activeId]);

  // Socket: connect once, listen for new messages — append live if it's the
  // open thread, always refresh the list (unread badge / preview / ordering).
  useEffect(() => {
    const socket = getChatSocket();
    const onMessage = (msg) => {
      refreshConversations();
      setMessages((prev) =>
        prev.length && msg.conversationId === activeIdRef.current
          ? [...prev, msg]
          : prev,
      );
    };
    socket.on("chat:message", onMessage);
    return () => socket.off("chat:message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!conversations.length) return;
    getChatSocket().emit("chat:join", { conversationIds: conversations.map((c) => c._id) });
  }, [conversations]);

  const openConversation = async (id) => {
    setActiveId(id);
    setLoadingMessages(true);
    try {
      const res = await getMessagesApi(id);
      setMessages(res.data.messages || []);
      markReadApi(id).then(refreshConversations);
    } finally {
      setLoadingMessages(false);
    }
  };

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  const startDm = async (userId) => {
    const res = await getOrCreateDmApi(userId);
    setContactsOpen(false);
    refreshConversations();
    openConversation(res.data.conversation._id);
  };

  const send = async () => {
    if (!activeId || (!text.trim() && files.length === 0)) return;
    setSending(true);
    try {
      const res = await sendMessageApi({ conversationId: activeId, text: text.trim(), files });
      setMessages((prev) => [...prev, res.data.message]);
      setText("");
      setFiles([]);
      if (fileInputRef.current) fileInputRef.current.value = "";
      refreshConversations();
    } catch (err) {
      // eslint-disable-next-line no-alert
      alert(err?.response?.data?.message || "Couldn't send that message.");
    } finally {
      setSending(false);
    }
  };

  const conversationLabel = (c) =>
    c.type === "dm" ? c.otherMember?.name || "Unknown" : c.name || "Group";

  return (
    <>
      <PageTitle activeMenu="Messages" motherMenu="Chat" />
      <div className={`${panelClass} flex`} style={{ height: "calc(100vh - 180px)" }}>
        {/* ── Conversation list ─────────────────────────────────────────── */}
        <div className="flex w-80 shrink-0 flex-col border-r border-gray-200">
          <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
            <h2 className="text-sm font-semibold text-gray-900">Messages</h2>
            <button
              type="button"
              className="rounded-full bg-cyan-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-cyan-700"
              onClick={() => setContactsOpen(true)}
            >
              + New
            </button>
          </div>
          <div className="flex-1 overflow-y-auto">
            {convLoading ? (
              <div className="p-6 text-center text-sm text-gray-400">Loading…</div>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-sm text-gray-400">
                No conversations yet. Start one with "+ New".
              </div>
            ) : (
              conversations.map((c) => (
                <button
                  key={c._id}
                  type="button"
                  onClick={() => openConversation(c._id)}
                  className={`flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left hover:bg-gray-50 ${
                    activeId === c._id ? "bg-cyan-50" : ""
                  }`}
                >
                  <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-xs font-bold text-cyan-700">
                    {initials(conversationLabel(c))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center justify-between">
                      <span className="truncate text-sm font-medium text-gray-900">
                        {conversationLabel(c)}
                      </span>
                      <span className="ml-2 shrink-0 text-[10px] text-gray-400">
                        {fmtDay(c.lastMessageAt)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between">
                      <span className="truncate text-xs text-gray-500">
                        {c.lastMessagePreview || "No messages yet"}
                      </span>
                      {c.unreadCount > 0 && (
                        <span className="ml-2 shrink-0 rounded-full bg-cyan-600 px-1.5 py-0.5 text-[10px] font-bold text-white">
                          {c.unreadCount}
                        </span>
                      )}
                    </div>
                  </div>
                </button>
              ))
            )}
          </div>
        </div>

        {/* ── Thread ─────────────────────────────────────────────────────── */}
        <div className="flex flex-1 flex-col">
          {!active ? (
            <div className="flex flex-1 items-center justify-center text-sm text-gray-400">
              Select a conversation to start chatting.
            </div>
          ) : (
            <>
              <div className="border-b border-gray-200 px-5 py-3">
                <h3 className="text-sm font-semibold text-gray-900">
                  {conversationLabel(active)}
                </h3>
              </div>
              <div className="flex-1 overflow-y-auto px-5 py-4">
                {loadingMessages ? (
                  <div className="py-10 text-center text-sm text-gray-400">Loading…</div>
                ) : (
                  <div className="space-y-3">
                    {messages.map((m) => {
                      const mine = String(m.senderId?._id || m.senderId) === String(meId);
                      return (
                        <div key={m._id} className={`flex ${mine ? "justify-end" : "justify-start"}`}>
                          <div
                            className={`max-w-[70%] rounded-2xl px-3 py-2 text-sm ${
                              mine ? "bg-cyan-600 text-white" : "bg-gray-100 text-gray-800"
                            }`}
                          >
                            {!mine && (
                              <div className="mb-0.5 text-[11px] font-semibold opacity-70">
                                {m.senderId?.name}
                              </div>
                            )}
                            {m.text && <div className="whitespace-pre-wrap">{m.text}</div>}
                            {(m.attachments || []).map((a, i) => (
                              <a
                                key={i}
                                href={`${process.env.REACT_APP_API_URL || "http://localhost:5001"}${a.url}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                className={`mt-1 flex items-center gap-1 truncate text-xs underline ${
                                  mine ? "text-white" : "text-cyan-700"
                                }`}
                              >
                                📎 {a.fileName}
                              </a>
                            ))}
                            <div
                              className={`mt-1 text-right text-[10px] ${mine ? "text-cyan-100" : "text-gray-400"}`}
                            >
                              {fmtTime(m.createdAt)}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                    <div ref={bottomRef} />
                  </div>
                )}
              </div>
              <div className="border-t border-gray-200 p-3">
                {files.length > 0 && (
                  <div className="mb-2 flex flex-wrap gap-2">
                    {files.map((f, i) => (
                      <span
                        key={i}
                        className="rounded-full bg-gray-100 px-2 py-1 text-xs text-gray-600"
                      >
                        {f.name}{" "}
                        <button
                          type="button"
                          className="ml-1 text-gray-400 hover:text-gray-700"
                          onClick={() => setFiles((prev) => prev.filter((_, idx) => idx !== i))}
                        >
                          ×
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                <div className="flex items-end gap-2">
                  <input
                    ref={fileInputRef}
                    type="file"
                    multiple
                    className="hidden"
                    id="chat-file-input"
                    onChange={(e) => setFiles(Array.from(e.target.files || []))}
                  />
                  <button
                    type="button"
                    className="rounded-full border border-gray-300 px-3 py-2 text-gray-500 hover:bg-gray-50"
                    onClick={() => fileInputRef.current?.click()}
                    title="Attach file"
                  >
                    📎
                  </button>
                  <textarea
                    rows={1}
                    className="flex-1 resize-none rounded-xl border border-gray-300 px-3 py-2 text-sm outline-none focus:border-cyan-500"
                    placeholder="Type a message…"
                    value={text}
                    onChange={(e) => setText(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        send();
                      }
                    }}
                  />
                  <button
                    type="button"
                    disabled={sending || (!text.trim() && files.length === 0)}
                    onClick={send}
                    className="rounded-full bg-cyan-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-50"
                  >
                    Send
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>

      {/* ── New chat picker ─────────────────────────────────────────────── */}
      {contactsOpen && (
        <div
          className="fixed inset-0 z-[1000] flex items-center justify-center bg-slate-900/40 p-4"
          onClick={() => setContactsOpen(false)}
        >
          <div
            className="max-h-[70vh] w-full max-w-sm overflow-hidden rounded-2xl bg-white shadow-2xl"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between border-b border-gray-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-gray-900">Start a chat</h3>
              <button
                type="button"
                className="text-xs text-gray-500"
                onClick={() => setContactsOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="max-h-[55vh] overflow-y-auto">
              {contacts.map((c) => (
                <button
                  key={c._id}
                  type="button"
                  className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-2.5 text-left hover:bg-gray-50"
                  onClick={() => startDm(c._id)}
                >
                  <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-cyan-100 text-xs font-bold text-cyan-700">
                    {initials(c.name)}
                  </div>
                  <div>
                    <div className="text-sm font-medium text-gray-900">{c.name}</div>
                    <div className="text-xs text-gray-400">{c.role}</div>
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatPage;
